import { LocationApiAdapter } from '../adapters/LocationApiAdapter';
import { CreateLocationRequest, Location, LocationFilters, LocationPage, Product } from '../models/Location';

export interface UpdateLocationRequest {
  code: string;
  type_id: number;
  rack_id?: number | null;
  shelf?: number | null;
  slot?: number | null;
}

export class LocationService {
  constructor(private apiAdapter: LocationApiAdapter) {}

  async getAllLocations(filters: LocationFilters = {}): Promise<Location[]> {
    return this.apiAdapter.getLocations(filters);
  }

  async getLocationsPaginated(filters: LocationFilters = {}): Promise<LocationPage> {
    const { searchTerm, limit } = filters;

    if (limit && limit > 100) {
      throw new Error('El límite no puede ser mayor a 100');
    }

    if (searchTerm && searchTerm.length < 2) {
      throw new Error('El término de búsqueda debe tener al menos 2 caracteres');
    }

    return this.apiAdapter.getLocationsPaginated(filters);
  }

  async getProducts(): Promise<Product[]> {
    return this.apiAdapter.getProducts();
  }

  async createLocation(data: CreateLocationRequest): Promise<Location> {
    // 🎯 Validaciones de negocio - Solo código y tipo son obligatorios
    if (!data.code || data.code.trim().length < 2) {
      throw new Error('El código de ubicación debe tener al menos 2 caracteres');
    }

    if (!data.type_id) {
      throw new Error('El tipo de ubicación es obligatorio');
    }

    // 🎯 Validar que type_id sea válido (1 = Almacén, 2 = Picking)
    if (![1, 2].includes(data.type_id)) {
      throw new Error('Tipo de ubicación inválido');
    }

    // 🎯 Aplicar valores por defecto si no se proporcionan
    const locationData: CreateLocationRequest = {
      ...data,
    };

    return this.apiAdapter.createLocation(locationData);
  }

  async updateLocation(id: number, data: UpdateLocationRequest): Promise<Location> {
    // 🎯 Validaciones de negocio - Solo código y tipo son obligatorios
    if (!data.code || data.code.trim().length < 2) {
      throw new Error('El código de ubicación debe tener al menos 2 caracteres');
    }

    if (!data.type_id) {
      throw new Error('El tipo de ubicación es obligatorio');
    }

    // 🎯 Validar que type_id sea válido (1 = Almacén, 2 = Picking)
    if (![1, 2].includes(data.type_id)) {
      throw new Error('Tipo de ubicación inválido');
    }

    return this.apiAdapter.updateLocation(id, data);
  }

  async deleteLocation(id: number): Promise<void> {
    // 🎯 Validación básica
    if (!id || id <= 0) {
      throw new Error('ID de ubicación inválido');
    }

    return this.apiAdapter.deleteLocation(id);
  }

  // 🎯 Acciones de bloqueo/desbloqueo utilizando endpoints dedicados
  async lockLocation(id: number): Promise<Location> {
    if (!id || id <= 0) {
      throw new Error('ID de ubicación inválido');
    }
    return this.apiAdapter.lockLocation(id);
  }

  async unlockLocation(id: number): Promise<Location> {
    if (!id || id <= 0) {
      throw new Error('ID de ubicación inválido');
    }
    return this.apiAdapter.unlockLocation(id);
  }

  async printLabel(id: number): Promise<Blob> {
    if (!id || id <= 0) {
      throw new Error('ID de ubicación inválido');
    }
    return this.apiAdapter.printLabel(id);
  }

  async adjustStock(payload: {
    product_id: number;
    qty: number;
    location_id: number;
    description?: string;
    batch_id?: number;
  }): Promise<void> {
    if (!payload?.product_id || typeof payload.qty !== 'number' || !payload.location_id) {
      throw new Error('Parámetros inválidos para ajustar stock');
    }
    return this.apiAdapter.adjustStock(payload);
  }
}
